import React, { useState } from 'react';
import Filters from '../../../components/Filter';
import { AccessParamList } from '../../../typings/Params';
import { NavigationProp, useNavigation } from '@react-navigation/native';
import FilterInput from '../../../components/Filter/components/FilterInput';
import { translate } from '../../../services/translate';
import ActingBodiesDropdown from '../../../components/Dropdowns/ActingBodiesDropdown';
import ActingBodiesUnitsDropdown from '../../../components/Dropdowns/ActingBodiesUnitsDropdown';
import MyPhoneInput from '../../../components/MyPhoneInput';
import MyCpfInput from '../../../components/MyCpfInput';
import RolesDropdown from '../../../components/Dropdowns/RolesDropdown';
import BlockNatureDropdown from './BlockNatureDropdown';

interface Props {
    filters: AccessParamList['Users'];
    setFilters: React.Dispatch<React.SetStateAction<AccessParamList['Users']>>;
    textFilter: string;
    setTextFilter: React.Dispatch<React.SetStateAction<string>>;
    authenticatedUser?: AuthenticatedUser | null;
}

function UsersFilter({ filters, setFilters, setTextFilter, textFilter, authenticatedUser }: Props) {
    const [selectedActingBodyId, setSelectedActingBodyId] = useState<number>(filters.actingBodyId ?? 0);
    const [selectedActingBodyUnitId, setSelectedActingBodyUnitId] = useState<number>(filters.actingBodyUnitId ?? 0);
    const [phone, setPhone] = useState<string>(filters.phone ?? '');
    const [document, setDocument] = useState<string>(filters.document ?? '');
    const [role, setRole] = useState<number>(filters.role ?? 0);
    const [blockNature, setBlockNature] = useState<string>(filters.userBlockNature ?? '');

    const navigation = useNavigation<NavigationProp<AccessParamList, 'Users'>>();

    const handleCleanFilter = () => {
        setTextFilter('');
        setSelectedActingBodyUnitId(0);
        setPhone('');
        setDocument('');
        setSelectedActingBodyId(0);
        setRole(0);
        setBlockNature('');
    };

    const handleFilter = () => {
        setFilters({
            ...filters,
            textFilter: textFilter !== '' ? textFilter : undefined,
            document: document !== '' ? document : undefined,
            phone: phone !== '' ? phone : undefined,
            role: role !== 0 ? role : undefined,
            actingBodyId: selectedActingBodyId !== 0 ? selectedActingBodyId : undefined,
            actingBodyUnitId: selectedActingBodyUnitId !== 0 ? selectedActingBodyUnitId : undefined,
            userBlockNature: blockNature !== '' ? blockNature : undefined,
        });

        navigation.setParams({
            textFilter: textFilter !== '' ? textFilter : undefined,
            document: document !== '' ? document : undefined,
            phone: phone !== '' ? phone : undefined,
            role: role !== 0 ? role : undefined,
            actingBodyId: selectedActingBodyId !== 0 ? selectedActingBodyId : undefined,
            actingBodyUnitId: selectedActingBodyUnitId !== 0 ? selectedActingBodyUnitId : undefined,
            userBlockNature: blockNature !== '' ? blockNature : undefined,
            page: 1
        });
    };

    function getActingBodyId() {
        if (authenticatedUser?.isAdmin
            || authenticatedUser?.permissions.access_edit_user_all_acting_bodies
            || authenticatedUser?.permissions.access_remove_user_all_acting_bodies
        ) {
            return selectedActingBodyId;
        }

        return authenticatedUser?.actingBodyUnit.actingBodyId ?? 0;
    }

    return (
        <Filters onCleanFilters={handleCleanFilter} onFilters={handleFilter}>
            {(authenticatedUser?.isAdmin
                || authenticatedUser?.permissions.access_edit_user_all_acting_bodies
                || authenticatedUser?.permissions.access_remove_user_all_acting_bodies
            ) &&
                <ActingBodiesDropdown
                    value={selectedActingBodyId}
                    setValue={setSelectedActingBodyId}
                    zIndex={4} />
            }
            {(authenticatedUser?.isAdmin
                || authenticatedUser?.permissions.access_edit_user_acting_body_units
                || authenticatedUser?.permissions.access_edit_user_all_acting_bodies
                || authenticatedUser?.permissions.access_remove_user_acting_body
                || authenticatedUser?.permissions.access_remove_user_all_acting_bodies
            ) &&
                <ActingBodiesUnitsDropdown
                    actingBodyId={getActingBodyId()}
                    value={selectedActingBodyUnitId}
                    setValue={setSelectedActingBodyUnitId}
                    zIndex={3} />
            }
            <FilterInput
                label={translate('searchUser')}
                value={textFilter}
                placeholder={translate('userFilterPlaceholder')}
                onChange={text => {
                    setTextFilter(text);
                }}
            />

            <MyCpfInput
                value={document}
                onChangeText={setDocument}
                viewStyle={{ flexGrow: 1, minWidth: 200, }}
                isInvalid={false}
                setIsInvalid={() => { }}
            />

            <MyPhoneInput
                label={translate('phone')}
                placeholder={translate('phone')}
                value={phone}
                onChangeText={setPhone}
                invalid={() => false}
                viewStyle={{
                    flexGrow: 1, minWidth: 200,
                }}
                labelStyle={{
                    fontSize: 16
                }}
            />
            <RolesDropdown
                actingBodyId={getActingBodyId()}
                value={role}
                setValue={setRole}
                zIndex={2} />
            <BlockNatureDropdown
                value={blockNature}
                setValue={setBlockNature}
                zIndex={1}
            />
        </Filters>
    );
}

export default UsersFilter;
